import { Check, History, Maximize2, MessageCircleMore, MessageSquarePlus, Settings2, Sparkles, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import usePlutoConversationHistory from "../../hooks/usePlutoConversationHistory";
import usePlutoReply, { PlutoReplyError, PlutoReplyStopped } from "../../hooks/usePlutoReply";
import { Button } from "../ui/button";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { PlutoComposer } from "./PlutoComposer";
import { PlutoConversationHistoryList } from "./PlutoConversationHistoryList";
import { plutoChatMessageFromStored, usePluto } from "./PlutoProvider";
import { PlutoResearchThread } from "./PlutoResearchThread";
import { PlutoWaitingPrompts } from "./PlutoWaitingPrompts";
import { PlutoWritingPreferencesPanel } from "./PlutoWritingPreferencesPanel";
import { getPlutoPageContext } from "./page-context";
import { canRestoreStoredTranscript, canStopPlutoTurn, isMissingConversationError } from "./pluto-session";

export const PlutoAssistant = () => {
  const location = useLocation();
  const {
    abandonConversationHydration,
    appendUserTurn,
    canQueuePrompt,
    closeAssistant,
    completeAssistantTurn,
    conversationId,
    discardPendingTurn,
    failPendingTurn,
    hydrateConversation,
    isAwaitingReply,
    isBusy,
    isOpen,
    messages,
    openAssistant,
    openConversation,
    pendingHydrationConversationId,
    queuedPrompt,
    queuedPromptSequence,
    replyError,
    startNewConversation,
    removeWaitingPrompt,
    stopPendingTurn,
    submitPrompt,
    waitingPrompts,
  } = usePluto();
  const [dismissedContextIds, setDismissedContextIds] = useState<string[]>([]);
  const [showWritingPreferences, setShowWritingPreferences] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const handledPromptSequence = useRef(0);
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const {
    mutateAsync: createReply,
    activeToolName,
    toolSteps,
    streamingText,
    reasoningText,
    stopReply,
  } = usePlutoReply();
  const {
    data: storedConversation,
    error: storedConversationError,
    isError: storedConversationUnreadable,
    isFetching: isReadingStoredConversation,
  } = usePlutoConversationHistory(pendingHydrationConversationId);
  const pageContexts = getPlutoPageContext(location.pathname, location.search);
  const activeContexts = pageContexts.filter((context) => !dismissedContextIds.includes(context.id));
  const companyContext = activeContexts.find((context) => context.id === "company");
  const isPlutoWorkspace = location.pathname === "/pluto";

  useEffect(() => {
    setDismissedContextIds([]);
  }, [location.pathname]);

  useEffect(() => {
    if (!canRestoreStoredTranscript(storedConversation, isReadingStoredConversation)) return;
    hydrateConversation(storedConversation.messages.map(plutoChatMessageFromStored));
  }, [hydrateConversation, isReadingStoredConversation, storedConversation]);

  useEffect(() => {
    if (!storedConversationUnreadable) return;
    if (isMissingConversationError(storedConversationError)) startNewConversation();
    else abandonConversationHydration();
  }, [abandonConversationHydration, startNewConversation, storedConversationError, storedConversationUnreadable]);

  // The streamed text is part of what the transcript is showing, so an answer
  // that grows past the fold keeps following itself rather than scrolling once
  // when the turn starts and again when it finishes.
  useEffect(() => {
    const transcript = transcriptRef.current;
    if (!transcript) return;
    transcript.scrollTop = transcript.scrollHeight;
  }, [isAwaitingReply, messages, reasoningText, streamingText]);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key !== "/") return;
      event.preventDefault();
      openAssistant();
    };

    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [openAssistant]);

  useEffect(() => {
    if (!queuedPrompt || queuedPromptSequence === 0 || handledPromptSequence.current === queuedPromptSequence) return;
    handledPromptSequence.current = queuedPromptSequence;
    const sequence = queuedPromptSequence;

    appendUserTurn(queuedPrompt);
    void createReply({
      prompt: queuedPrompt,
      contexts: activeContexts,
      ...(conversationId ? { conversationId } : {}),
    })
      .then((response) => {
        if (handledPromptSequence.current !== sequence) return;
        completeAssistantTurn({
          text: response.text,
          conversationId: response.conversationId,
          usedToolNames: response.usedToolNames,
          ...(response.draft ? { draft: response.draft } : {}),
        });
      })
      .catch((error: unknown) => {
        if (handledPromptSequence.current !== sequence) return;
        if (error instanceof PlutoReplyStopped) {
          // A question the server never received was neither answered nor
          // stored, so the turn leaves the transcript rather than sitting in it
          // as something the next load would not bring back.
          if (error.reachedServer) stopPendingTurn(error.partialText);
          else discardPendingTurn();
          return;
        }
        // A turn that failed after the answer started streaming explains itself;
        // anything else is a transport fault the reader can do nothing with.
        failPendingTurn(
          error instanceof PlutoReplyError ? error.message : "The reply service is unavailable. Please try again.",
        );
      });
  }, [
    activeContexts,
    appendUserTurn,
    completeAssistantTurn,
    conversationId,
    createReply,
    discardPendingTurn,
    failPendingTurn,
    queuedPrompt,
    queuedPromptSequence,
    stopPendingTurn,
  ]);

  return (
    <>
      {isPlutoWorkspace ? null : (
        <Button
          aria-label="Ask Pluto"
          className="fixed bottom-6 right-6 z-40 h-12 gap-2 rounded-full bg-stone-900 px-5 text-white shadow-[0_12px_32px_-12px_rgba(28,25,23,0.7)] hover:bg-stone-700"
          onClick={openAssistant}
        >
          <Sparkles aria-hidden="true" className="h-4 w-4" />
          Ask Pluto
          <span className="hidden rounded border border-white/20 px-1.5 py-0.5 text-[10px] text-stone-300 sm:inline">
            ⌘ /
          </span>
        </Button>
      )}

      <Sheet open={isOpen} onOpenChange={(open) => (open ? openAssistant() : closeAssistant())}>
        <SheetContent
          className="flex w-full flex-col gap-0 border-l border-stone-200 bg-[#faf9f7] p-0 sm:w-[460px]"
          overlayClassName="!bg-stone-950/15"
          side="right"
        >
          <SheetHeader className="border-b border-stone-200 bg-white px-5 py-4 text-left">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="mb-1 flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-stone-900 text-white">
                    <Sparkles aria-hidden="true" className="h-3.5 w-3.5" />
                  </span>
                  <SheetTitle className="text-lg font-semibold text-stone-900">Pluto</SheetTitle>
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900">
                    UI preview
                  </span>
                </div>
                <SheetDescription className="text-sm text-stone-600">
                  Your research assistant, grounded in the page you are viewing.
                </SheetDescription>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {messages.length > 0 ? (
                  <Button
                    aria-label="Start a new Pluto conversation"
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      setShowHistory(false);
                      startNewConversation();
                    }}
                  >
                    <MessageSquarePlus aria-hidden="true" className="h-4 w-4" />
                  </Button>
                ) : null}
                <Button
                  aria-label="Show earlier Pluto conversations"
                  aria-pressed={showHistory}
                  size="icon"
                  variant="ghost"
                  onClick={() => setShowHistory((visible) => !visible)}
                >
                  <History aria-hidden="true" className="h-4 w-4" />
                </Button>
                <Button
                  aria-label="Configure Pluto writing instructions"
                  aria-pressed={showWritingPreferences}
                  size="icon"
                  variant="ghost"
                  onClick={() => setShowWritingPreferences((visible) => !visible)}
                >
                  <Settings2 aria-hidden="true" className="h-4 w-4" />
                </Button>
                {isPlutoWorkspace ? (
                  <Button
                    aria-label="Expand Pluto to the workspace"
                    size="icon"
                    variant="ghost"
                    onClick={closeAssistant}
                  >
                    <Maximize2 aria-hidden="true" className="h-4 w-4" />
                  </Button>
                ) : null}
                <SheetClose asChild>
                  <Button aria-label="Close Pluto" size="icon" variant="ghost">
                    <X aria-hidden="true" className="h-4 w-4" />
                  </Button>
                </SheetClose>
              </div>
            </div>
          </SheetHeader>

          {showWritingPreferences ? (
            <PlutoWritingPreferencesPanel onBack={() => setShowWritingPreferences(false)} />
          ) : showHistory ? (
            <div className="flex-1 overflow-y-auto px-5 py-6">
              <PlutoConversationHistoryList
                activeConversationId={conversationId}
                onSelect={(selectedConversationId) => {
                  openConversation(selectedConversationId);
                  setShowHistory(false);
                }}
              />
            </div>
          ) : (
            <>
              <div className="border-b border-stone-200 bg-white px-5 py-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-stone-700">Context Pluto can use</span>
                  <span className="flex items-center gap-1 text-[11px] text-stone-500">
                    <Check aria-hidden="true" className="h-3 w-3" />
                    Visible and controllable
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {activeContexts.length > 0 ? (
                    activeContexts.map((context) => (
                      <button
                        className="group inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-stone-50 px-3 py-1.5 text-xs text-stone-700 transition-colors hover:border-stone-500 hover:bg-stone-100"
                        key={context.id}
                        type="button"
                        onClick={() => setDismissedContextIds((ids) => [...ids, context.id])}
                      >
                        <span className="font-medium">{context.label}</span>
                        <span className="max-w-36 truncate text-stone-500">{context.value}</span>
                        <X aria-hidden="true" className="h-3 w-3 text-stone-400 group-hover:text-stone-700" />
                      </button>
                    ))
                  ) : (
                    <p className="text-xs text-stone-500">No page context attached. Pluto will only use your prompt.</p>
                  )}
                </div>
              </div>

              <div className="flex-1 space-y-5 overflow-y-auto px-5 py-6" ref={transcriptRef}>
                {messages.length === 0 && !isAwaitingReply && !replyError ? (
                  <div className="max-w-sm">
                    <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-full border border-stone-300 bg-white">
                      <MessageCircleMore aria-hidden="true" className="h-4 w-4 text-stone-700" />
                    </span>
                    <h2 className="text-xl font-semibold text-stone-900">What should we investigate?</h2>
                    <p className="mt-2 text-sm leading-6 text-stone-600">
                      Ask about the current view, look for timing signals, or compare the companies you are reviewing.
                    </p>
                    {companyContext ? (
                      <div className="mt-5 flex flex-wrap gap-2">
                        <button
                          className="rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 transition-colors hover:border-stone-500 hover:bg-stone-50"
                          type="button"
                          onClick={() =>
                            submitPrompt(
                              "Write a sales email to this company on its strongest current timing signal, asking for a short meeting.",
                            )
                          }
                        >
                          Draft sales email
                        </button>
                        <button
                          className="rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 transition-colors hover:border-stone-500 hover:bg-stone-50"
                          type="button"
                          onClick={() =>
                            submitPrompt(
                              "Write a sales SMS to this company on its strongest current timing signal, asking for a short call.",
                            )
                          }
                        >
                          Draft sales SMS
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <PlutoResearchThread
                    activeToolName={activeToolName}
                    error={replyError}
                    isPending={isAwaitingReply}
                    messages={messages}
                    reasoningText={reasoningText}
                    streamingText={streamingText}
                    toolSteps={toolSteps}
                  />
                )}
              </div>

              <div className="border-t border-stone-200 bg-white px-4 py-4">
                {canStopPlutoTurn({ isAwaitingReply }) ? (
                  <button
                    className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg border border-stone-300 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50"
                    type="button"
                    onClick={stopReply}
                  >
                    <span aria-hidden className="size-2.5 rounded-[2px] bg-stone-700" />
                    Stop
                  </button>
                ) : null}
                <PlutoWaitingPrompts
                  isHeld={replyError !== null}
                  prompts={waitingPrompts}
                  onRemove={removeWaitingPrompt}
                />
                <PlutoComposer
                  disabled={isBusy && !canQueuePrompt}
                  placeholder={isBusy ? "Ask a follow-up — Pluto will get to it next…" : "Ask about this page…"}
                  onSubmit={submitPrompt}
                />
                {isBusy && !canQueuePrompt ? (
                  <p className="mt-2 text-xs text-stone-500">
                    Three questions are already waiting. Pluto answers them one at a time.
                  </p>
                ) : null}
                {isPlutoWorkspace ? (
                  <button
                    className="mt-3 block w-full text-center text-xs font-medium text-stone-600 underline decoration-stone-300 underline-offset-4 hover:text-stone-900"
                    type="button"
                    onClick={closeAssistant}
                  >
                    Expand to the full Pluto workspace
                  </button>
                ) : (
                  <Link
                    className="mt-3 block text-center text-xs font-medium text-stone-600 underline decoration-stone-300 underline-offset-4 hover:text-stone-900"
                    to="/pluto"
                    onClick={closeAssistant}
                  >
                    Open the full Pluto workspace
                  </Link>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};
